import { useEffect, useState } from 'react';
import { Button, Chip } from '@mui/material';
import { GridPaginationModel } from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { AmcAttributionCriteria } from '../../types/Criteria/AmcAttributionCriteria';
import { useAxiosCancelToken } from '../../hooks/AxiosCancelToken';
import { useDataGridRowCount, useDataGridPageable } from '../../hooks/DataGrid';
import ApiService from '../../ApiService';
import Block from '../../components/Block';
import CDGrid from '../../components/CDGridPro';
import Column from '../../components/Column';
import Row from '../../components/Row';
import Utils from '../../components/Utils';

import AmcAttributionCreateFormDialog from './AmcAttributionCreateFormDialog';
import AmcPageHeader from '../amc/partials/AmcPageHeader';
import usePageTitle from '../../hooks/usePageTitle';

export default function AmcAttributionList() {
    const navigate = useNavigate();
    const withAxiosCancelTokenRequestConfig = useAxiosCancelToken();

    const [amcAttributionFormDialogOpen, setAmcAttributionFormDialogOpen] = useState<boolean>(false);

    const [criteria, setCriteria] = useState<AmcAttributionCriteria>({
        agencyId: null,
        advertiserId: null,
    });

    // build & render title
    usePageTitle({ segments: ['AMC', 'Attribution Results'] });

    const { pageable, paginationModel, sortModel, setPaginationModel, setSortModel } = useDataGridPageable();

    const { data, isFetching, isLoading, refetch } = useQuery({
        queryKey: ['amcAttributions', criteria, pageable],
        queryFn: async () => {
            try {
                const { data } = await ApiService.getAmcAttributions(
                    criteria.agencyId || null,
                    criteria.advertiserId || null,
                    pageable,
                    withAxiosCancelTokenRequestConfig()
                );
                return data;
            } catch (error) {
                return null;
            }
        },
        enabled: false,
        refetchOnWindowFocus: false,
    });

    const rowCount = useDataGridRowCount(data?.totalElements);

    useEffect(() => {
        refetch();
    }, [criteria, pageable, refetch]);

    // Reset page to 0 when critera changes
    useEffect(() => {
        setPaginationModel((prev: GridPaginationModel) => ({ ...prev, page: 0 }));
    }, [criteria, setPaginationModel]);

    return (
        <>
            <AmcPageHeader criteria={criteria} setCriteria={setCriteria} />

            <Block sx={{ height: '100%' }}>
                <Column gap={2} sx={{ height: '100%' }}>
                    <Row justifyContent="flex-end">
                        <Button
                            variant="contained"
                            size="medium"
                            color="primary"
                            onClick={() => {
                                setAmcAttributionFormDialogOpen(true);
                            }}
                        >
                            Add Offline Attribution
                        </Button>
                    </Row>

                    <CDGrid
                        paginationMode="server"
                        sortingMode="server"
                        rowCount={rowCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        sortModel={sortModel}
                        onSortModelChange={setSortModel}
                        loading={isFetching || isLoading}
                        columns={[
                            {
                                filterable: false,
                                width: 110,
                                field: 'date',
                                headerName: 'Workflow Date',
                                headerClassName: 'grid-header',
                                valueGetter: (value: Date) => {
                                    return new Date(value).toLocaleDateString('en-US', {
                                        month: 'short',
                                        year: 'numeric',
                                    });
                                },
                            },
                            {
                                filterable: false,
                                flex: 1,
                                minWidth: 200,
                                field: 'salesDataUpload',
                                headerName: 'Date Range',
                                valueGetter: (value: { beginDate: Date; endDate: Date }) => {
                                    const dateRanges: string[] = [];

                                    if (value?.beginDate) {
                                        dateRanges.push(Utils.formatDate(value.beginDate));
                                    }

                                    if (value?.endDate) {
                                        dateRanges.push(Utils.formatDate(value.endDate));
                                    }

                                    return dateRanges.join(' - ');
                                },
                                valueFormatter: (params: { value: any }) => {
                                    return params.value;
                                },
                            },
                            {
                                filterable: false,
                                width: 120,
                                field: 'status',
                                headerName: 'Status',
                                headerClassName: 'grid-header',
                                renderCell: (params) => (
                                    <Chip
                                        label={params.value}
                                        size="small"
                                        variant={params.value === 'Published' ? 'filled' : 'outlined'}
                                        color={params.value === 'Published' ? 'success' : 'warning'}
                                    />
                                ),
                            },
                            {
                                filterable: false,
                                width: 160,
                                field: 'dealer',
                                headerName: 'Advertiser',
                                headerClassName: 'grid-header',
                                valueGetter: (value: { dealerName: string }) => {
                                    return value?.dealerName;
                                },
                            },
                            {
                                filterable: false,
                                width: 150,
                                field: 'campaignName',
                                headerName: 'Campaign Name',
                                headerClassName: 'grid-header',
                            },
                            {
                                filterable: false,
                                width: 110,
                                field: 'dataSetName',
                                headerName: 'Data Type',
                                headerClassName: 'grid-header',
                            },
                            {
                                filterable: false,
                                width: 150,
                                field: 'recordsSubmitted',
                                headerName: 'Total Identities Submitted',
                                headerClassName: 'grid-header',
                            },
                            {
                                filterable: false,
                                width: 150,
                                field: 'matchedAmazonRecords',
                                headerName: 'Matched-to-Amazon Identities',
                                headerClassName: 'grid-header',
                            },
                            {
                                filterable: false,
                                width: 150,
                                field: 'attributedUserRecords',
                                headerName: 'Ad Exposed Identities',
                                headerClassName: 'grid-header',
                            },
                            {
                                filterable: false,
                                width: 110,
                                field: 'adBudget',
                                type: 'number',
                                headerName: 'Ad Budget',
                                headerClassName: 'grid-header',
                            },
                            {
                                filterable: false,
                                width: 110,
                                field: 'adSpend',
                                type: 'number',
                                headerName: 'Ad Spend',
                                headerClassName: 'grid-header',
                            },
                            {
                                filterable: false,
                                width: 110,
                                field: 'gross',
                                type: 'number',
                                headerName: 'Gross',
                                headerClassName: 'grid-header',
                            },
                        ]}
                        autoPageSize={true}
                        rows={data?.content || []}
                        onRowClick={(params) => {
                            navigate('' + params.row.id);
                        }}
                        slots={{
                            toolbar: null,
                        }}
                    />
                </Column>
            </Block>

            {amcAttributionFormDialogOpen && (
                <AmcAttributionCreateFormDialog
                    open={amcAttributionFormDialogOpen}
                    onClose={() => {
                        refetch();
                        setAmcAttributionFormDialogOpen(false);
                    }}
                />
            )}
        </>
    );
}
